import React, { useRef, useState } from 'react';
import cn from 'classnames';
import { useAppSelector } from '../../effects/useAppSelector';
import { useAppDispatch } from '../../effects/useAppDispatch';
import { loanActions } from '../../actions/loan.actions';
import { LoanField } from '../../types/loan/LoanField';
import { ChipList } from '../filters/ChipList';
import { ClickOutside } from '../common/ClickOutside';
import IconSVG from '../../styles/svg-icons';

interface Props {
    disabled?: boolean;
    onSearch: () => void;
}

export function IssuerSearchInput({ disabled = false, onSearch }: Props) {
    const dispatch = useAppDispatch();
    const allIssuers = useAppSelector(s => s.entities.loanFieldValues.values[LoanField.Issuer]) ?? [];
    const issuersSearch = useAppSelector(s => s.loan.issuersSearch);

    const refInput = useRef<HTMLInputElement>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [inputFocus, setInputFocus] = useState(false);
    const [dropdownIndex, setDropdownIndex] = useState(0);

    const filteredLookup = searchTerm.trim()
        ? [
              searchTerm,
              ...allIssuers.filter(
                  i =>
                      i.toLowerCase().includes(searchTerm.toLowerCase()) &&
                      !issuersSearch.includes(i),
              ),
          ]
        : [];

    const addIssuer = (value: string) => {
        const trimmed = value.trim();
        if (!trimmed || issuersSearch.includes(trimmed)) return;
        dispatch(loanActions.issuerSearchChange([...issuersSearch, trimmed]));
        setSearchTerm('');
        setDropdownIndex(0);
        onSearch();
        refInput.current?.focus();
    };

    const handleRemoveChip = (i: number) => {
        dispatch(loanActions.issuerSearchChange(issuersSearch.filter((_, idx) => idx !== i)));
        onSearch();
        refInput.current?.focus();
    };

    const handleClearAll = () => {
        setSearchTerm('');
        setDropdownIndex(0);
        dispatch(loanActions.issuerSearchChange([]));
        onSearch();
        refInput.current?.focus();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' && !searchTerm && issuersSearch.length) {
            handleRemoveChip(issuersSearch.length - 1);
            return;
        }
        if (!filteredLookup.length) return;
        if (e.key === 'Enter') {
            addIssuer(filteredLookup[dropdownIndex]);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setDropdownIndex(i => Math.max(0, i - 1));
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            setDropdownIndex(i => Math.min(filteredLookup.length - 1, i + 1));
        }
    };

    const handleClickOutside = (e: MouseEvent) => {
        e.stopPropagation();
        setSearchTerm('');
        setDropdownIndex(0);
        setInputFocus(false);
    };

    return (
        <ClickOutside onClick={handleClickOutside}>
            <div
                className={cn('control-search-wrapper control-search-wrapper-issuer', {
                    'control-search-focus': inputFocus,
                    disabled,
                })}
            >
                <div className="form-control-search-btn">
                    <i className="icon icon-search" />
                </div>
                <ChipList list={issuersSearch} onRemove={handleRemoveChip} />
                <input
                    ref={refInput}
                    className="form-control form-control-search"
                    type="text"
                    value={searchTerm}
                    placeholder={issuersSearch.length ? '' : 'Search By Issuer'}
                    disabled={disabled}
                    onChange={e => { setSearchTerm(e.target.value); setDropdownIndex(0); }}
                    onKeyDown={handleKeyDown}
                    onFocus={() => setInputFocus(true)}
                />
                {(!!searchTerm || !!issuersSearch.length) && (
                    <button className="btn-close" disabled={disabled} onClick={handleClearAll}>
                        <IconSVG name="close" width={16} height={16} />
                    </button>
                )}
                {!!filteredLookup.length && (
                    <div className="search-lookup">
                        <table>
                            <tbody>
                                {filteredLookup.map((item, i) => (
                                    <tr
                                        key={i}
                                        className={cn({ active: dropdownIndex === i })}
                                        onClick={() => addIssuer(item)}
                                    >
                                        {i === 0 ? (
                                            <>
                                                <td className="col-main"><strong>Custom search:</strong></td>
                                                <td className="col-ellipsis">{item}</td>
                                            </>
                                        ) : (
                                            <td className="col-main" colSpan={2}>{item}</td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </ClickOutside>
    );
}
